/**
 * The Auger side of mail: which saved Email Thread a Gmail thread is, the open
 * deals its people are on, and three steps up from there: linking a thread (an
 * Email Thread document, private, no messages), saving it (an Email document with
 * the text per message — the shape the Pipedrive mail import left, so what is
 * saved here reads like what is already there) and sharing it (the thread and its
 * Email documents visible to the team). Gmail itself stays with each interface.
 *
 * Gmail holds no custom data on a thread, so the association lives here: a
 * document holding a `gmail_thread` source, related (`related_to`) to the deal,
 * organization or person it is about.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import { declaredSchemaFieldKeys } from "../collections/records.js"
import {
  fetchOpenDealsForPeople,
  fetchThreadLinks,
  fetchThreadMessageSources,
} from "../gql/mail.js"
import type { Query } from "../index.js"
import type { CreatedRecord, createRecordsService, FullRecord } from "../services/records.js"
import { type createSourcesService, sourceHolder } from "../services/sources.js"

type Records = ReturnType<typeof createRecordsService>
type Sources = ReturnType<typeof createSourcesService>

/** A saved thread: its document and the records it is related to. */
export type ThreadLink = NonNullable<
  ResultOf<typeof fetchThreadLinks>["record_sources"][number]["record"]
>

/** An open deal a person is on. */
export type DealMatch = ResultOf<typeof fetchOpenDealsForPeople>["records"][number]

/** Who a message is from and to, as Auger person ids. */
export interface SaveMessage {
  /** The Gmail message id. */
  id: string
  /** The RFC 5322 Message-ID: the same in every mailbox. */
  messageId?: string | null
  subject?: string | null
  sentAt?: string | null
  direction: "sent" | "received"
  from?: number | null
  to?: number[]
  cc?: number[]
  inReplyTo?: string | null
  references?: string | null
  body?: string | null
}

export interface LinkThreadInput {
  /** The Email Thread schema. */
  threadSchemaId: number
  /** The Gmail thread. */
  threadId: string
  subject: string
  lastMessageAt?: string | null
  /** The deals, organizations or people the thread is about. */
  relatedTo: number[]
  /** The people on the thread. */
  participants: number[]
}

export interface SaveThreadInput extends LinkThreadInput {
  /** The Email schema. */
  messageSchemaId: number
  messages: SaveMessage[]
}

export interface LinkedThread {
  /** The thread's document: new, or the one that already held the thread. */
  record: CreatedRecord | FullRecord
  /** True when a document already held the thread. */
  existing: boolean
  /** Link keys left out because the schemas don't declare them. */
  undeclared: string[]
}

export interface SavedThread extends LinkedThread {
  /** Messages created by this call. */
  messagesCreated: number
}

export function createMailService({
  records,
  sources,
  query,
}: {
  records: Records
  sources: Sources
  query: Query
}) {
  /** The saved document and related records for each Gmail thread id that has
   *  one. A thread nobody saved is absent. */
  async function findLinked(threadIds: readonly string[]): Promise<Map<string, ThreadLink>> {
    const found = new Map<string, ThreadLink>()
    const ids = [...new Set(threadIds)]
    if (!ids.length) return found
    const { record_sources } = await query(fetchThreadLinks, { threadIds: ids })
    for (const { source_id, record } of record_sources) {
      if (record && !found.has(source_id)) found.set(source_id, record)
    }
    return found
  }

  /** The open deals each person is on, as the deal's `person`, by person id. */
  async function dealsForPeople(personIds: readonly number[]): Promise<Map<number, DealMatch[]>> {
    const byPerson = new Map<number, DealMatch[]>()
    const ids = [...new Set(personIds)]
    if (!ids.length) return byPerson
    const { records: deals } = await query(fetchOpenDealsForPeople, { personIds: ids })
    for (const deal of deals) {
      for (const { linked_id } of deal.links) {
        byPerson.set(linked_id, [...(byPerson.get(linked_id) ?? []), deal])
      }
    }
    return byPerson
  }

  const live = (record: FullRecord | null | undefined): record is FullRecord =>
    record != null && record.type === "document" && record.archived_at == null

  /** The live document that holds a Gmail thread, or null. */
  async function findThread(threadId: string): Promise<FullRecord | null> {
    const id = await sources.holder({
      sourceType: "gmail_thread",
      sourceId: threadId,
      recordType: "document",
    })
    if (id == null) return null
    const record = await records.get(id)
    return live(record) ? record : null
  }

  const unique = (ids: readonly number[]) => [...new Set(ids)]

  const linker =
    (undeclared: Set<string>) => (declared: Set<string>, key: string, ids: readonly number[]) => {
      if (!ids.length) return {}
      if (declared.has(key)) return { [key]: unique(ids) }
      undeclared.add(key)
      return {}
    }

  /** Link a thread: its Email Thread document, private and without messages, so
   *  the thread can be related to a deal, organization or person. For a thread
   *  already linked, the records it is newly related to. */
  async function linkThread(input: LinkThreadInput): Promise<LinkedThread> {
    const declared = declaredSchemaFieldKeys({
      type: "document",
      schema_id: input.threadSchemaId,
    })
    const undeclared = new Set<string>()
    const link = linker(undeclared)

    const related = unique(input.relatedTo)
    const threadData = {
      subject: input.subject,
      ...(input.lastMessageAt ? { last_message_at: input.lastMessageAt } : {}),
      ...link(declared, "participants", input.participants),
      ...link(declared, "related_to", related),
    }

    let existing = await findThread(input.threadId)
    let thread: CreatedRecord | FullRecord
    if (existing) {
      thread = existing
      if (related.length && declared.has("related_to")) {
        await records.update(existing.id, { related_to: { add: related } }, { read: false })
      }
    } else {
      try {
        thread = await records.create({
          type: "document",
          name: input.subject,
          schema_id: input.threadSchemaId,
          visibility: "private",
          data: {
            ...threadData,
            sources: [{ sourceType: "gmail_thread", sourceId: input.threadId }],
          },
        })
      } catch (err) {
        // Another link won the race: take the document that holds the thread.
        const holder = sourceHolder(err, "gmail_thread", input.threadId)
        if (holder === undefined) throw err
        const held = await records.get(holder)
        if (!live(held)) throw err
        existing = held
        thread = held
      }
    }
    return { record: thread, existing: existing != null, undeclared: [...undeclared] }
  }

  /** Save a thread: link it, then keep an Email document, text included, for each
   *  message. A message already saved without its text gets the text. The Email
   *  documents are as visible as the thread. */
  async function saveThread(input: SaveThreadInput): Promise<SavedThread> {
    const linked = await linkThread(input)
    const thread = linked.record
    const declared = declaredSchemaFieldKeys({
      type: "document",
      schema_id: input.messageSchemaId,
    })
    const undeclared = new Set(linked.undeclared)
    const link = linker(undeclared)
    const related = unique(input.relatedTo)

    const saved = new Set<string>()
    const bodiless = new Map<string, number>()
    if (linked.existing) {
      const { records: children } = await query(fetchThreadMessageSources, {
        threadRecordId: thread.id,
      })
      for (const child of children) {
        const hasBody = !!(child.data as { content?: unknown } | null)?.content
        for (const s of child.sources) {
          saved.add(s.source_id)
          if (!hasBody) bodiless.set(s.source_id, child.id)
        }
      }
    }

    for (const m of input.messages) {
      const id = bodiless.get(m.id)
      if (id != null && m.body) await records.update(id, { content: m.body }, { read: false })
    }

    const fresh = input.messages.filter((m) => !saved.has(m.id))
    const created = await records.createMany(
      fresh.map((m) => ({
        type: "document",
        name: m.subject || input.subject,
        schema_id: input.messageSchemaId,
        parent_id: thread.id,
        visibility: thread.visibility,
        data: {
          subject: m.subject || input.subject,
          direction: m.direction,
          ...(m.sentAt ? { sent_at: m.sentAt } : {}),
          ...(m.messageId ? { message_id: m.messageId } : {}),
          ...(m.inReplyTo ? { in_reply_to: m.inReplyTo } : {}),
          ...(m.references ? { references: m.references } : {}),
          ...(m.body ? { content: m.body } : {}),
          ...link(declared, "from", m.from ? [m.from] : []),
          ...link(declared, "to", m.to ?? []),
          ...link(declared, "cc", m.cc ?? []),
          ...link(declared, "related_to", related),
          sources: [{ sourceType: "gmail_message", sourceId: m.id }],
        },
      }))
    )

    return { ...linked, undeclared: [...undeclared], messagesCreated: created.length }
  }

  /** Share a saved thread: the thread and its Email documents become visible to
   *  the team. A thread that is only linked cannot be shared. */
  async function shareThread(threadId: string): Promise<FullRecord> {
    const thread = await findThread(threadId)
    if (!thread) throw new Error("Save the thread before sharing it")
    const { records: children } = await query(fetchThreadMessageSources, {
      threadRecordId: thread.id,
    })
    if (!children.length) throw new Error("Save the thread before sharing it")
    await records.update(thread.id, { visibility: "shared" }, { read: false })
    await records.updateMany(
      children.map((c) => c.id),
      { visibility: "shared" }
    )
    return { ...thread, visibility: "shared" }
  }

  return { findLinked, dealsForPeople, findThread, linkThread, saveThread, shareThread }
}
